import { useEffect, useState, type FormEvent } from "react";
import { useNavigate } from "react-router";
import { VOICE_LIMITS, type Channel, type CommunityStructure, type VoiceParticipant } from "@atrium/shared";
import { cn } from "../../lib/cn";
import { useUI } from "../../lib/uiStore";
import { getSocket } from "../../lib/socket";
import { Avatar } from "../../components/ui/Avatar";
import { Button, IconButton } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { toast } from "../../components/ui/Toast";
import { Tooltip } from "../../components/ui/Tooltip";
import { IconBack, IconChatLine, IconEdit, IconHandUp, IconHandUpActive, IconInviteToSpeak, IconStage, IconUsers } from "../../components/icons";
import { PrivateMark } from "../communities/ChannelSidebar";
import { useMe } from "../auth/AuthProvider";
import { ChannelView } from "../communities/ChannelView";
import { permsOf } from "../communities/hooks";
import { useVoiceModActions } from "../communities/VoiceRoomView";
import { useStageTopic, useVoice, useVoiceStates } from "./VoiceProvider";
import { ActionsButton, RoomGrid, WithActions, type ParticipantAction } from "./RoomGrid";
import { VoiceControls, useShareBlockedReason } from "./VoiceControls";
import { useVoiceStatesStore } from "./stores";

type Ack = { ok: true; data?: unknown } | { ok: false; error: string };

type StageRequest = { event: "stage:speaker"; p: { channelId: string; userId: string; speaker: boolean } } | { event: "stage:topic"; p: { channelId: string; topic: string } };

function emitStage(req: StageRequest) {
  return new Promise<void>((resolve) => {
    const s = getSocket();
    if (!s) return resolve();
    const done = (res: Ack) => {
      if (!res.ok) toast.error(res.error);
      resolve();
    };
    if (req.event === "stage:speaker") s.emit("stage:speaker", req.p, done);
    else s.emit("stage:topic", req.p, done);
  });
}

function TopicEditor({ channelId, topic, onDone }: { channelId: string; topic: string; onDone: () => void }) {
  const [value, setValue] = useState(topic);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    await emitStage({ event: "stage:topic", p: { channelId, topic: value } });
    onDone();
  };
  return (
    <form onSubmit={submit} className="flex w-full max-w-xl items-center gap-2">
      <Input autoFocus value={value} maxLength={VOICE_LIMITS.stageTopic} onChange={(e) => setValue(e.target.value)} placeholder="What's this stage about?" aria-label="Stage topic" onKeyDown={(e) => e.key === "Escape" && onDone()} />
      <Button type="submit" variant="primary">
        Save
      </Button>
      <Button variant="ghost" onClick={onDone}>
        Cancel
      </Button>
    </form>
  );
}

function AudienceMember({ p, actions }: { p: VoiceParticipant; actions: ParticipantAction[] }) {
  return (
    <WithActions p={p} actions={actions}>
      <li className="group relative flex w-24 flex-col items-center gap-1.5 rounded-lg p-2 text-center hover:bg-surface">
        <span className="relative">
          <Avatar src={p.user.avatarUrl} name={p.user.displayName} size="lg" />
          {p.handRaisedAt && (
            <Tooltip label="Asked to speak">
              <span className="absolute -right-1.5 -top-1.5 flex size-6 items-center justify-center rounded-full bg-accent text-accent-fg ring-2 ring-bg" aria-label="Asked to speak">
                <IconHandUpActive className="icon size-3.5" aria-hidden />
              </span>
            </Tooltip>
          )}
        </span>
        <span className="w-full truncate text-sm text-fg-2">{p.user.displayName}</span>
        <ActionsButton p={p} actions={actions} className="absolute right-0 top-0 opacity-0 focus:opacity-100 group-hover:opacity-100 data-[state=open]:opacity-100 [@media(hover:none)]:opacity-100" />
      </li>
    </WithActions>
  );
}

/**
 * Stage channel: speakers on top (they connect to everyone), the audience
 * below (listen-only; they connect to speakers only, so a stage holds more
 * people than a voice channel). Moderators invite people up and set the topic.
 */
export function StageView({ structure, channel, mobile }: { structure: CommunityStructure; channel: Channel; mobile?: boolean }) {
  const voice = useVoice();
  const me = useMe();
  const navigate = useNavigate();
  const [mobileChat, setMobileChat] = useState(false);
  // Desktop remembers the chat panel; on phones chat replaces the stage, so it always starts closed.
  const desktopChat = useUI((s) => s.voiceChatOpen);
  const chatOpen = mobile ? mobileChat : desktopChat;
  const setChatOpen = (open: boolean) => (mobile ? setMobileChat(open) : useUI.getState().setVoiceChatOpen(open));
  const [editingTopic, setEditingTopic] = useState(false);
  const participants = useVoiceStates(structure.community.id, structure.voiceStates).filter((p) => p.channelId === channel.id);
  const topic = useStageTopic(channel.id);
  const perms = permsOf(channel.permissions, structure.community.isOwner);
  const moderator = perms.muteMembers || perms.manageChannels;
  const here = voice.channelId === channel.id;
  const self = here ? voice.self : null;
  const speakers = participants.filter((p) => p.speaker);
  const audience = participants
    .filter((p) => !p.speaker)
    .sort((a, b) => (a.handRaisedAt && b.handRaisedAt ? a.handRaisedAt.localeCompare(b.handRaisedAt) : a.handRaisedAt ? -1 : b.handRaisedAt ? 1 : 0));
  const raised = audience.filter((p) => p.handRaisedAt).length;
  const shareReason = useShareBlockedReason(channel, structure);
  const modActions = useVoiceModActions(structure, channel);
  const full = participants.length >= (channel.userLimit > 0 ? Math.min(channel.userLimit, VOICE_LIMITS.stageParticipants) : VOICE_LIMITS.stageParticipants) && !here && !perms.disconnectMembers;

  // The topic lives with the live stage; fetch it when opening the view.
  useEffect(() => {
    getSocket()?.emit("stage:info", { channelId: channel.id }, (res) => {
      if (res.ok && res.data) useVoiceStatesStore.getState().setTopic(channel.id, res.data.topic);
    });
  }, [channel.id]);

  const actions = (p: VoiceParticipant): ParticipantAction[] => {
    const out: ParticipantAction[] = [];
    if (moderator && p.user.id !== me.id) {
      if (p.speaker) out.push({ label: "Move to audience", icon: IconUsers, run: () => void emitStage({ event: "stage:speaker", p: { channelId: channel.id, userId: p.user.id, speaker: false } }) });
      else out.push({ label: "Invite to speak", icon: IconInviteToSpeak, run: () => void emitStage({ event: "stage:speaker", p: { channelId: channel.id, userId: p.user.id, speaker: true } }) });
    }
    return [...out, ...modActions(p)];
  };

  const setSpeaker = (speaker: boolean) => void emitStage({ event: "stage:speaker", p: { channelId: channel.id, userId: me.id, speaker } });
  const raiseHand = (raised: boolean) => getSocket()?.emit("stage:hand", { raised });

  const joinButton = (
    <Button
      variant="primary"
      size="lg"
      icon={IconStage}
      disabled={!perms.voice || full || voice.status === "connecting"}
      loading={voice.status === "connecting" && voice.channelId === channel.id}
      onClick={() => void voice.join(channel.id, structure.community.id, channel.name)}
    >
      {full ? "Stage full" : "Join stage"}
    </Button>
  );

  const stage = (
    <div className="flex h-full min-w-0 flex-1 flex-col bg-bg">
      <header className="flex h-12 shrink-0 items-center gap-2 border-b border-line px-3">
        {mobile && <IconButton label="Back" icon={IconBack} tooltip={false} onClick={() => navigate(`/c/${structure.community.id}`)} />}
        <IconStage className="icon size-5 shrink-0 text-fg-3" aria-hidden />
        <h1 className="truncate text-base font-semibold text-fg">{channel.name}</h1>
        {channel.isPrivate && <PrivateMark className="text-fg-3" />}
        <span className="ml-2 text-sm text-fg-3 tabular">
          {speakers.length}/{VOICE_LIMITS.stageSpeakers} speaking · {audience.length} listening
        </span>
        <div className="ml-auto">
          <IconButton label={chatOpen ? "Hide chat" : "Show chat"} icon={IconChatLine} active={chatOpen} onClick={() => setChatOpen(!chatOpen)} />
        </div>
      </header>
      <div className={cn("flex min-h-0 flex-1 flex-col items-center gap-6 overflow-y-auto p-4 md:p-6", (participants.length === 0 || !speakers.some((p) => p.streaming)) && "justify-center")}>
        {participants.length === 0 ? (
          <>
            <div className="text-center">
              <div className="mx-auto mb-3 flex size-14 items-center justify-center rounded-2xl bg-surface-2">
                <IconStage className="icon size-7 text-fg-2" />
              </div>
              <h2 className="text-lg font-semibold">This stage is quiet</h2>
              <p className="mt-1 text-sm text-fg-3">{channel.topic || "Speakers are heard by everyone; the audience listens and can ask to speak."}</p>
            </div>
            {!here && joinButton}
          </>
        ) : (
          <>
            {(topic || channel.topic || moderator) && (
              <div className="flex w-full max-w-5xl flex-col items-center gap-1 text-center">
                {editingTopic ? (
                  <TopicEditor channelId={channel.id} topic={topic} onDone={() => setEditingTopic(false)} />
                ) : topic ? (
                  <div className="flex items-center gap-1">
                    <h2 className="text-xl font-semibold">{topic}</h2>
                    {moderator && <IconButton size="sm" label="Edit topic" icon={IconEdit} onClick={() => setEditingTopic(true)} />}
                  </div>
                ) : (
                  moderator && (
                    <Button size="sm" variant="ghost" icon={IconEdit} onClick={() => setEditingTopic(true)}>
                      Add a topic
                    </Button>
                  )
                )}
                {channel.topic && <p className="text-sm text-fg-3">{channel.topic}</p>}
              </div>
            )}

            {speakers.length > 0 ? (
              <div className={cn("flex w-full flex-col", speakers.some((p) => p.streaming) && "flex-1")}>
                <RoomGrid participants={speakers} actions={actions} />
              </div>
            ) : (
              <p className="text-sm text-fg-3">No one's on stage yet.</p>
            )}

            {!here && joinButton}

            {audience.length > 0 && (
              <section className="w-full max-w-5xl" aria-label="Audience">
                <h3 className="mb-2 flex items-center gap-2 text-sm font-medium text-fg-2">
                  Audience <span className="text-fg-3 tabular">{audience.length}</span>
                  {raised > 0 && (
                    <span className="inline-flex items-center gap-1 text-accent">
                      <IconHandUpActive className="icon size-4" aria-hidden />
                      {raised} asked to speak
                    </span>
                  )}
                </h3>
                <ul className="flex flex-wrap gap-1">
                  {audience.map((p) => (
                    <AudienceMember key={p.socketId} p={p} actions={actions(p)} />
                  ))}
                </ul>
              </section>
            )}
          </>
        )}
      </div>
      {here && (
        <footer className="flex shrink-0 justify-center px-4 pb-5 pt-3">
          {self?.speaker ? (
            <VoiceControls shareReason={shareReason}>
              <Button size="lg" icon={IconUsers} onClick={() => setSpeaker(false)}>
                Move to audience
              </Button>
            </VoiceControls>
          ) : (
            <VoiceControls shareReason={shareReason}>
              {moderator ? (
                <Button size="lg" variant="primary" icon={IconInviteToSpeak} onClick={() => setSpeaker(true)} disabled={speakers.length >= VOICE_LIMITS.stageSpeakers}>
                  Speak
                </Button>
              ) : (
                <Button size="lg" variant={self?.handRaisedAt ? "secondary" : "primary"} icon={self?.handRaisedAt ? IconHandUpActive : IconHandUp} onClick={() => raiseHand(!self?.handRaisedAt)}>
                  {self?.handRaisedAt ? "Lower hand" : "Request to speak"}
                </Button>
              )}
            </VoiceControls>
          )}
        </footer>
      )}
    </div>
  );

  if (!chatOpen) return stage;
  if (mobile)
    return (
      <div className="flex h-full min-w-0 flex-1 flex-col">
        <ChannelView structure={structure} channel={channel} mobile embedded onClose={() => setChatOpen(false)} />
      </div>
    );
  return (
    <div className="flex h-full min-w-0 flex-1">
      {stage}
      <div className="flex w-[380px] shrink-0 border-l border-line xl:w-[420px]">
        <ChannelView structure={structure} channel={channel} embedded onClose={() => setChatOpen(false)} />
      </div>
    </div>
  );
}
